/**
 * Shared in-memory reactive table store: applies schema.ts events to Vue refs synchronously (so
 * every `computed(() => store.query(...))` across the app keeps working unchanged) and answers
 * `.select().where(...)` queries against those refs. Used by both the real persisted adapter
 * (src/db/localFirstStore.ts, backed by real SQLite) and the test-only double
 * (src/testing/vueLivestoreTestDouble.js, no persistence) -- one implementation, so a fix here
 * (like the WHERE-clause parsing bug below) can't drift between the two.
 */
import { ref, computed, triggerRef, type Ref } from 'vue';
import { rawMaterializers } from '@/livestore/schema.js';

export interface StoreEvent {
  args: Record<string, unknown>;
  name: string;
}

interface SqlQueryLike {
  asSql(): { bindValues?: readonly unknown[]; query: string };
}

function tableNameFromSql(sql: string): string | null {
  const m = /(?:FROM|INTO|UPDATE)\s+['"](\w+)['"]/i.exec(sql);
  return m?.[1] ?? null;
}

function tableNameFromQuery(q: unknown): string | null {
  try {
    if (typeof (q as SqlQueryLike)?.asSql !== 'function') return null;
    return tableNameFromSql((q as SqlQueryLike).asSql().query);
  } catch {
    return null;
  }
}

/** Parses "col1" = ? AND "col2" = ? ... into { col1: args[0], col2: args[1] }, consuming one positional arg per match. */
function extractEqConditions(
  whereClause: string,
  args: readonly unknown[],
): Record<string, unknown> {
  const conditions: Record<string, unknown> = {};
  let i = 0;
  for (const cond of whereClause.split(/\bAND\b/i)) {
    const trimmed = cond.trim();
    const eqIdx = trimmed.indexOf('=');
    if (eqIdx <= 0) continue;
    const key = trimmed
      .slice(0, eqIdx)
      .trim()
      .replace(/^["']|["']$/g, '');
    const rhs = trimmed.slice(eqIdx + 1).trim();
    if (key && rhs === '?' && i < args.length) conditions[key] = args[i++];
  }
  return conditions;
}

function parseWhere(q: unknown): Record<string, unknown> | null {
  try {
    const query = q as SqlQueryLike;
    if (typeof query?.asSql !== 'function') return null;
    const { bindValues: args = [], query: sql } = query.asSql();
    if (!args.length) return null;
    const whereMatch = /WHERE\s+(.+)/is.exec(sql);
    if (!whereMatch) return null;
    const conditions = extractEqConditions(whereMatch[1], args);
    return Object.keys(conditions).length ? conditions : null;
  } catch {
    return null;
  }
}

type Row = Record<string, unknown>;
type Verb = 'insert' | 'update' | 'delete';

function stripQuotes(s: string): string {
  return s.trim().replace(/^["']|["']$/g, '');
}

/** `INSERT INTO "t" ("a", "b") VALUES (?, ?)` -> { a: bindValues[0], b: bindValues[1] }. Column
 * order always matches placeholder order in this query builder's generated SQL. */
function parseInsertFields(sql: string, bindValues: readonly unknown[]): Row {
  const m = /INSERT INTO\s+['"]\w+['"]\s*\(([^)]+)\)/i.exec(sql);
  if (!m) return {};
  const fields: Row = {};
  m[1].split(',').forEach((col, i) => {
    if (i < bindValues.length) fields[stripQuotes(col)] = bindValues[i];
  });
  return fields;
}

/** `UPDATE "t" SET "a" = ?, "b" = ? WHERE "id" = ?` -> ({a,b} from the SET clause's own bind
 * slice, {id} from the WHERE clause's remaining bind slice) -- each half must consume only its
 * own placeholders since bindValues is [...SET values, ...WHERE values] in generation order.
 *
 * Uses keyword-position slicing instead of a single capture-group regex to avoid catastrophic
 * backtracking: the old `/\bSET\s+(.+?)\s+WHERE\s+(.+)$/is` had two overlapping `.+?`/`.+`
 * groups that the engine explored in O(n²) when WHERE wasn't found. */
function parseUpdateFieldsAndWhere(
  sql: string,
  bindValues: readonly unknown[],
): { fields: Row; where: Row } {
  const setMatch = /\bSET\b/i.exec(sql);
  const whereMatch = /\bWHERE\b/i.exec(sql);
  if (!setMatch || !whereMatch || whereMatch.index <= setMatch.index)
    return { fields: {}, where: {} };

  const setClause = sql.slice(setMatch.index + setMatch[0].length, whereMatch.index);
  const whereClause = sql.slice(whereMatch.index + whereMatch[0].length);

  const fields: Row = {};
  let i = 0;
  for (const part of setClause.split(',')) {
    const eqIdx = part.indexOf('=');
    if (eqIdx <= 0) continue;
    const key = stripQuotes(part.slice(0, eqIdx));
    if (part.slice(eqIdx + 1).trim() === '?' && i < bindValues.length)
      fields[key] = bindValues[i++];
  }
  const where = extractEqConditions(whereClause, bindValues.slice(i));
  return { fields, where };
}

function matches(row: Row, where: Row): boolean {
  const keys = Object.keys(where);
  return keys.length > 0 && keys.every(k => row[k] === where[k]);
}

/** Filters rows to those matching every key in `where`. Extracted to avoid deep nesting
 * (S2004) inside the closure-heavy `createInMemoryStore` → `query`/`useQuery` methods. */
function filterByWhere(rows: Row[], where: Row): Row[] {
  return rows.filter(r => matches(r, where));
}

function applyCreate(rows: Row[], fields: Row): Row[] {
  return [...rows, fields];
}
function applyUpdate(rows: Row[], fields: Row, where: Row): Row[] {
  if (!Object.keys(where).length) return rows;
  return rows.map(r => (matches(r, where) ? { ...r, ...fields } : r));
}
function applyDelete(rows: Row[], where: Row): Row[] {
  if (!Object.keys(where).length) return rows;
  return rows.filter(r => !matches(r, where));
}

/** Applies a single mutation (insert/update/delete) to the appropriate table ref.
 * Extracted to avoid deep nesting (S2004) inside `createInMemoryStore` → `commit` → for-loop. */
function applyMutation(refs: Record<string, Ref<Row[]>>, mutation: Mutation): void {
  const { fields, table, verb, where } = mutation;
  if (!refs[table]) return;
  if (verb === 'insert') refs[table].value = applyCreate(refs[table].value, fields);
  else if (verb === 'update') refs[table].value = applyUpdate(refs[table].value, fields, where);
  else if (verb === 'delete') refs[table].value = applyDelete(refs[table].value, where);
  triggerRef(refs[table]);
}

/**
 * Which table(s) a committed event mutates, and the exact fields/match-condition of each
 * resulting query, derived from the REAL materializer's query object(s) -- not the event's raw
 * top-level args. A single event can produce multiple queries with DIFFERENT field shapes (e.g.
 * v1.TimeEntryUnsealed: one UPDATE soft-deleting the original by id, one INSERT of a clone under
 * a different id) -- reusing the same raw args for every mutation previously merged the clone's
 * fields onto the original and inserted the clone under the original's id instead of its own.
 * Reading each query's own SQL + bindValues keeps every mutation scoped to what it actually does.
 */
type Mutation = { fields: Row; table: string; verb: Verb; where: Row };

/** Classifies one query object into its table/verb/fields/where, or null if it's not a
 * recognized INSERT/UPDATE/DELETE (or its SQL can't be parsed) -- split out of
 * mutationsForEvent purely to keep that function's branching under the project's complexity cap. */
function mutationForQuery(q: SqlQueryLike): Mutation | null {
  try {
    const { bindValues = [], query: sql } = q.asSql();
    const table = tableNameFromSql(sql);
    if (!table) return null;
    const s = sql.trimStart().toUpperCase();
    if (s.startsWith('INSERT')) {
      return { fields: parseInsertFields(sql, bindValues), table, verb: 'insert', where: {} };
    }
    if (s.startsWith('UPDATE')) {
      const { fields, where } = parseUpdateFieldsAndWhere(sql, bindValues);
      return { fields, table, verb: 'update', where };
    }
    if (s.startsWith('DELETE')) {
      const whereIdx = sql.search(/\bWHERE\b/i);
      const where = whereIdx >= 0 ? extractEqConditions(sql.slice(whereIdx + 5), bindValues) : {};
      return { fields: {}, table, verb: 'delete', where };
    }
    return null;
  } catch {
    return null; /* skip unparseable */
  }
}

function mutationsForEvent(event: StoreEvent): Mutation[] {
  const materialize = (
    rawMaterializers as unknown as Record<string, (args: Row) => SqlQueryLike | SqlQueryLike[]>
  )[event.name];
  if (!materialize) return [];
  const result = materialize(event.args);
  const queries = Array.isArray(result) ? result : [result];
  const out: Mutation[] = [];
  for (const q of queries) {
    const mutation = mutationForQuery(q);
    if (mutation) out.push(mutation);
  }
  return out;
}

export interface PersistenceHooks {
  /** Called once per table at store creation; resolved rows become the ref's initial value. */
  hydrate?(tableName: string): Promise<Row[]>;
  /** Fire-and-forget write-through after an in-memory commit already applied. */
  persist?(event: StoreEvent): void;
}

export function createInMemoryStore(tableNames: readonly string[], hooks: PersistenceHooks = {}) {
  const _refs: Record<string, Ref<Row[]>> = {};
  for (const name of tableNames) _refs[name] = ref([]);

  const hydrated = ref(false);
  const hydration = hooks.hydrate
    ? Promise.all(
        tableNames.map(async name => {
          _refs[name].value = await hooks.hydrate!(name);
        }),
      )
        .then(() => {
          hydrated.value = true;
        })
        .catch(err => {
          console.error('Store hydration failed:', err);
          hydrated.value = true;
        })
    : Promise.resolve().then(() => {
        hydrated.value = true;
      });

  const store = {
    commit(event: StoreEvent): void {
      for (const mutation of mutationsForEvent(event)) {
        applyMutation(_refs, mutation);
      }
      hooks.persist?.(event);
    },
    query(q: unknown): Row[] {
      const name = tableNameFromQuery(q);
      if (!name || !_refs[name]) return [];
      const where = parseWhere(q);
      if (!where) return _refs[name].value;
      return filterByWhere(_refs[name].value, where);
    },
  };

  function useQuery(q: unknown): Ref<Row[]> {
    const name = tableNameFromQuery(q);
    if (!name || !_refs[name]) return ref([]);
    const where = parseWhere(q);
    if (!where) return _refs[name];
    // Reactive filtered view -- .where() must be honored here too, not just in query(), or every
    // reactive read (useQuery) silently returns the full unfiltered table regardless of its
    // .where() clause while one-shot store.query() calls on the same table correctly filter.
    return computed(() => filterByWhere(_refs[name].value, where)) as unknown as Ref<Row[]>;
  }

  return { hydrated, hydration, store, useQuery };
}
